import type { ReactNode } from 'react';
import { ConfigProvider, theme as antTheme } from 'antd';
import { useThemeStore } from '../stores/themeStore';

interface ThemeProviderProps {
  children: ReactNode;
}

/**
 * Color scheme definitions for the 6 available themes
 * Each theme provides primary and accent colors
 */
const colorSchemes = {
  OCEAN_BLUE: {
    colorPrimary: '#1890ff',
    colorInfo: '#13c2c2',
    colorSuccess: '#52c41a',
    colorWarning: '#faad14',
    colorError: '#ff4d4f',
  },
  FOREST_GREEN: {
    colorPrimary: '#52c41a',
    colorInfo: '#a0d911',
    colorSuccess: '#389e0d',
    colorWarning: '#faad14',
    colorError: '#ff4d4f',
  },
  ROYAL_PURPLE: {
    colorPrimary: '#722ed1',
    colorInfo: '#9254de',
    colorSuccess: '#52c41a',
    colorWarning: '#faad14',
    colorError: '#ff4d4f',
  },
  SUNSET_ORANGE: {
    colorPrimary: '#fa8c16',
    colorInfo: '#ffc53d',
    colorSuccess: '#52c41a',
    colorWarning: '#fadb14',
    colorError: '#ff4d4f',
  },
  MIDNIGHT: {
    colorPrimary: '#1f1f1f',
    colorInfo: '#434343',
    colorSuccess: '#52c41a',
    colorWarning: '#faad14',
    colorError: '#ff4d4f',
  },
  CORPORATE: {
    colorPrimary: '#2f54eb',
    colorInfo: '#597ef7',
    colorSuccess: '#52c41a',
    colorWarning: '#faad14',
    colorError: '#ff4d4f',
  },
} as const;

/**
 * Theme provider component that wraps the app with Ant Design theme configuration
 * Supports dark/light mode and 6 color schemes
 */
export function ThemeProvider({ children }: ThemeProviderProps) {
  const { mode, colorScheme } = useThemeStore();

  const isDark =
    mode === 'DARK' ||
    (mode === 'SYSTEM' && window.matchMedia('(prefers-color-scheme: dark)').matches);

  const themeConfig = {
    algorithm: isDark ? antTheme.darkAlgorithm : antTheme.defaultAlgorithm,
    token: {
      ...colorSchemes[colorScheme],
      borderRadius: 6,
      fontFamily:
        '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
    },
    components: {
      Layout: {
        headerBg: isDark ? '#141414' : '#fff',
        siderBg: isDark ? '#141414' : '#fff',
        bodyBg: isDark ? '#000' : '#f5f5f5',
      },
      Card: {
        borderRadiusLG: 8,
      },
      Table: {
        borderRadius: 8,
      },
    },
  };

  return <ConfigProvider theme={themeConfig}>{children}</ConfigProvider>;
}
